import { computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import Ember from 'ember';
import Service, { inject as service } from '@ember/service';
import { resolve } from 'rsvp';
import moment from 'moment-timezone';
import { all, task, timeout } from 'ember-concurrency';
import pluralize from 'cio/utils/pluralize';
import ApiService from './api';
import DS from 'ember-data';
import AccountService from './account';
import BillingInfoModel from 'cio/models/billing-info';
import SubscriptionModel from 'cio/models/subscription';
import TaxRateModel from 'cio/models/tax-rate';
import TierModel from 'cio/models/tier';
import PlanModel, { PlanType } from 'cio/models/plan';
import CouponModel from 'cio/models/coupon';
import AlertsService from 'cio/services/alerts';
import RouterService from '@ember/routing/router-service';
import config from 'cio/config/environment';
import MomentService from 'cio/services/moment';
import { getUpgradePlanTypes } from 'cio/utils/billing/plans';
import { ProductApp } from 'cio/utils/product-app-info';
import isDestroyed from 'cio/utils/is-destroyed';

export const BASIC_2018_PLAN_SLUG = '201804_pricing_basic';
export const BASIC_PLAN_SLUG = '202209_pricing_basic';
export const ESSENTIALS_PLAN_SLUG = '202301_pricing_essentials';
export const CDP_STARTER_PLAN_SLUG = 'cdp_starter_202304';
export const MIN_ZIP_LENGTH = 3;
export const ZIP_INPUT_DEBOUNCE = 300;
export const FAIR_USAGE_POLICY = 1000000;
export const STANDARD_ADDITIONAL_MESSAGES_RATE = 0.00012;

export default class BillingService extends Service {
  @service api!: ApiService;
  @service store!: DS.Store;
  @service account!: AccountService;
  @service alerts!: AlertsService;
  @service router!: RouterService;
  @service moment!: MomentService;

  // Ember Data models/fragments that will be set on init, and are available as sum of billing-related information
  billingInfo?: BillingInfoModel;
  subscription?: SubscriptionModel;
  taxRate?: TaxRateModel;
  tiers?: DS.RecordArray<TierModel>;
  plans?: DS.RecordArray<PlanModel>;
  coupon?: CouponModel;

  // paramPlan is actually plan set by URL query param when visiting subscription settings
  paramPlan?: PlanModel | null;

  initialized = false;

  // TODO: Stores when there was a complete attempt of fetching billing info,
  // regardless if it was successful or not, to be able to display a loading state.
  // Also stores the current running initialization promise to prevent multiple
  // requests from being made at the same time.
  // This is intended to be a temporary solution while we test the impact on
  // performance on changes on this request. Should probably be refactored
  // into a `task` in the near future.
  hasCompletedBillingInfoRequest = false;
  initializationPromise: Promise<any> | null = null;

  @computed('plans.[]', 'billingInfo.pinnedPlan')
  get pinnedPlan() {
    if (this.billingInfo == null || isDestroyed(this.billingInfo)) {
      return null;
    }
    return this.plans?.findBy('id', this.billingInfo?.belongsTo('pinnedPlan').id());
  }

  @computed('plans.[]')
  get basicPlan() {
    return this.plans?.findBy('slug', BASIC_PLAN_SLUG);
  }

  @computed('plans.[]')
  get basic2018Plan() {
    return this.plans?.findBy('slug', BASIC_2018_PLAN_SLUG);
  }

  @computed('plans.[]')
  get cdpStarterPlan() {
    return this.plans?.findBy('slug', CDP_STARTER_PLAN_SLUG);
  }

  @computed('plans.[]')
  get essentialsPlan() {
    return this.plans?.findBy('slug', ESSENTIALS_PLAN_SLUG);
  }

  @computed('tiers')
  get pastTier() {
    return this.tiers?.find((t) => t.past);
  }

  @computed('tiers')
  get currentTier() {
    return this.tiers?.find((t) => t.current);
  }

  @computed('tiers')
  get nextTier() {
    return this.tiers?.find((t) => t.next);
  }

  get startDateInFuture() {
    return (this.billingInfo?.pinnedStartDate ?? 0) > this.moment.currentTimestamp;
  }

  get formattedStartDate() {
    return moment.unix(this.billingInfo?.pinnedStartDate ?? 0).format('Do MMMM YYYY');
  }

  @computed('nextTier', 'currentTier', 'account.current.usage.highWatermarkCustomersTotal')
  get currentTierOverage() {
    let customersOverCurrentTierMinimum =
      this.account.current.usage.get('highWatermarkCustomersTotal') - (this.currentTier?.minProfilesCount ?? 0);
    let currentTierNextTierDifference =
      (this.nextTier?.minProfilesCount ?? 0) - (this.currentTier?.minProfilesCount ?? 0);
    let currentTierPercentageUsed = Math.round((customersOverCurrentTierMinimum / currentTierNextTierDifference) * 100);
    return `${currentTierPercentageUsed}%`;
  }

  @computed('billingInfo.{basic,premium,trial}', 'subscription.plan.name', 'paramPlan')
  get planNameShortened() {
    let { billingInfo, subscription } = this;
    let plan;

    if (billingInfo == null || subscription == null || isDestroyed(billingInfo) || isDestroyed(subscription)) {
      return '';
    }

    if (billingInfo?.basic) {
      // main Customer.io Essentials plan as of Jan 2023:
      plan = this.plans?.findBy('id', this.subscription?.plan.id)?.name ?? 'Essentials';
    } else if (billingInfo?.trial && billingInfo?.premium) {
      // premium trial: use plan name based on URL param, fallback to generic "Premium Trial"
      plan = this.paramPlan ? this.paramPlan.name : 'Premium';
    } else {
      // any active custom plan (both premium, non-premium):
      let planModel = subscription?.belongsTo('plan')?.value() as PlanModel | null;
      plan = planModel?.get('name') ?? '';
    }

    if (billingInfo?.premiumTrial) {
      return `${plan} Trial`;
    }
    return plan;
  }

  @computed('billingInfo.trial', 'planNameShortened')
  get planName() {
    let { billingInfo, planNameShortened } = this;
    if (billingInfo?.trial) {
      if (this.trialSuspendingSoon) {
        return `${planNameShortened} Expiring Soon`;
      } else if (billingInfo?.trialSuspended) {
        return `${planNameShortened} Expired`;
      }
    } else {
      if (billingInfo?.overdue) {
        return `${planNameShortened} Past Due`;
      }
    }
    return planNameShortened;
  }

  @computed('account.current.suspendTrialAt')
  get daysLeftInTrial() {
    return moment.unix(this.account.current.suspendTrialAt).startOf('day').diff(moment().startOf('day'), 'days');
  }

  @computed('account.current.suspendTrialAt')
  get formattedTimeLeftInTrial() {
    return moment.unix(this.account.current.suspendTrialAt).fromNow();
  }

  @computed('daysLeftInTrial', 'account.current.accountState')
  get trialSuspendingSoon() {
    return this.daysLeftInTrial <= 3 && this.account.current.accountState === 'free';
  }

  get trialExpired() {
    return (
      this.account.current.suspendTrialAt != null && moment.unix(this.account.current.suspendTrialAt).isBefore(moment())
    );
  }

  @computed(
    'billingInfo.{overdue,trialSuspended,trial}',
    'trialSuspendingSoon',
    'daysLeftInTrial',
    'formattedTimeLeftInTrial',
    'account.current.accountState',
  )
  get planSummary() {
    let { billingInfo } = this;

    if (billingInfo?.overdue) {
      return 'Your account is overdue';
    } else if (billingInfo?.trialSuspended) {
      return `Your trial ended ${Math.abs(this.daysLeftInTrial)} ${pluralize(
        this.daysLeftInTrial,
        'day',
      )} ago. Choose a plan above to continue sending messages.`;
    } else if (billingInfo?.trial) {
      return `Your trial expires ${this.formattedTimeLeftInTrial}`;
    } else {
      return `Your account is ${this.account.current.accountState}.`;
    }
  }

  @computed('subscription.plan.planType', 'billingInfo.premiumTrial')
  get upgradePlanTypes(): Record<ProductApp, PlanType> {
    return getUpgradePlanTypes(
      this.billingInfo?.premiumTrial ?? false,
      this.account.planFeatures.messaging.expired,
      this.subscription?.plan.planType,
    );
  }

  async initialize({ refresh = false, ignoreErrors = false, paramPlanSlug = '', code = '' } = {}) {
    if (this.initialized && !refresh) {
      return resolve();
    }

    if (this.initializationPromise != null) {
      return this.initializationPromise;
    }

    let data = paramPlanSlug ? { extra_plan: paramPlanSlug } : {};

    let initializationPromise = new Promise<void>((resolve, reject) => {
      this.api
        .request(`billing/info?code=${code}`, {
          ignoreErrors,
          pathScope: 'account',
          data,
        })
        .then((res) => {
          // Remove empty keys from the response, like "null"
          // They error out the serializers when this.store.pushPayload is
          // called in Ember Data 4.8:
          Object.keys(res).forEach((k) => {
            if (res[k] == null) {
              delete res[k];
            }
          });

          this.store.pushPayload(res);
          this.setBillingProperties(res, paramPlanSlug);
          resolve(res);
        })
        .catch((error) => {
          if (ignoreErrors) {
            resolve();
          } else {
            reject(error);
          }
        })
        .finally(() => {
          if (this.isDestroyed) {
            return;
          }
          this.set('hasCompletedBillingInfoRequest', true);
          this.initializationPromise = null;
        });
    });

    this.initializationPromise = initializationPromise;

    return initializationPromise;
  }

  setBillingProperties(
    res: { billing_info?: Record<string, any>; subscription?: Record<string, any> },
    paramPlanSlug?: string,
  ) {
    // there's never more than one billing-info, subscription, coupon Ember Data model instances in the store
    this.setProperties({
      billingInfo:
        res.billing_info?.id != null
          ? this.store.peekRecord('billing-info', res.billing_info.id)
          : this.store.peekAll('billing-info').firstObject,
      subscription:
        res.subscription?.id != null
          ? this.store.peekRecord('subscription', res.subscription.id)
          : this.store.peekAll('subscription').firstObject,
      coupon: this.store.peekAll('coupon').firstObject,
      plans: this.store.peekAll('plan') as DS.RecordArray<PlanModel>,
      paramPlan: !isEmpty(paramPlanSlug) ? this.store.peekAll('plan').findBy('slug', paramPlanSlug) : null,
      taxRate: this.store.peekAll('taxRate').firstObject,
      tiers: this.store.peekAll('tier'),
      initialized: true,
    });
  }

  pollForSubscriptionTask = task(async (type: string) => {
    let ATTEMPTS_ALLOWED = 15;
    let attempts = 0;

    let requestPath = `billing/subscription_${type}`;

    while (attempts < ATTEMPTS_ALLOWED) {
      let res;
      let stripeSetupError = '';

      try {
        res = await this.api.request(requestPath, { pathScope: 'account', ignoreErrors: true });

        if (!res.error) {
          return;
        }

        stripeSetupError = res.error;
      } catch (e) {}

      attempts++;

      if (stripeSetupError) {
        throw new Error(stripeSetupError);
      }

      // allowing testing without long waits
      if (!Ember.testing) {
        await timeout(1000);
      }
    }

    // empty error is enough; any error means that success response wasn't obtained after enough tries
    throw new Error();
  });

  updateTaxRate = task(async ({ country, state, zipCode }: { country: string; state: string; zipCode: string }) => {
    let response = await this.api.request(`billing/tax_rate?country=${country}&state=${state}&zip_code=${zipCode}`, {
      pathScope: 'account',
    });

    this.set('taxRate', response?.tax_rate?.rate);
  });

  getSetupIntent = task(async () => {
    let response = await this.api.request('billing/setup_intent', { pathScope: 'account' });
    return response.setup_intent_client_secret;
  });

  updateCard = task({ drop: true }, async ({ setupIntent }: { setupIntent: Record<string, any> }) => {
    try {
      await this.account.current.updateCard({ setupIntent });
      this.alerts.sticky('subscription.updateCard.inProgress', 'Your card is updating, please wait …');
      await this.pollForSubscriptionTask.perform('complete');
      await this.initialize({ refresh: true });
      this.alerts.remove('subscription.updateCard.inProgress');
      this.alerts.success('subscription.updateCard.success', 'Card updated');
    } catch (res: any) {
      let error = (res?.errors?.[0] as string) || res;
      let errorMessage =
        (error.message as string) ||
        error.detail ||
        'An error occurred. Please contact win@customer.io for help setting up your subscription.';
      this.alerts.remove('subscription.updateCard.inProgress');
      this.alerts.error('subscription.updateCard.error', errorMessage);
    }
  });

  startSubscription = task(
    { drop: true },
    async ({ setupIntent }: { setupIntent: Record<string, any> }, code: string | null) => {
      let confirmationType = this.billingInfo?.activeSubscription ? 'complete' : 'exists';
      let newPlan = this.pinnedPlan ?? this.essentialsPlan;
      let couponCode = !isEmpty(code) ? code : this.billingInfo?.pinnedCouponCode;
      try {
        await this.account.current.subscribe({ planId: newPlan?.id, setupIntent, couponCode: couponCode ?? '' });
        let alertMessage = 'Your information is being processed, please wait…';
        if (this.startDateInFuture) {
          alertMessage = 'Your information is being saved, please wait…';
        }
        this.alerts.sticky('subscription.subscribe.inProgress', alertMessage);
        await this.pollForSubscriptionTask.perform(confirmationType);
        await all([
          this.initialize({ refresh: true }),
          this.store.queryRecord('account', {}), // refreshing account to remove traces of "trial" or previous plan state
        ]);
        let newSubscriptionPeriod = (await this.subscription?.get('annual')) ? 'Annual' : 'Monthly';
        this.alerts.remove('subscription.subscribe.inProgress');
        let successMessage = `<b>Your account is ready to go.</b> <br />You're now subscribed to the ${newPlan?.name} ${newSubscriptionPeriod} plan.`;
        if (this.startDateInFuture) {
          successMessage = `<b>Your account is ready to go.</b> <br /> You're now subscribed to the ${newPlan?.name} ${newSubscriptionPeriod} plan.<br /> The card on file will be automatically charged on your plan start date of ${moment
            .unix(this.billingInfo?.pinnedStartDate ?? 0)
            .format('LL')}.`;
        }
        await this.router.transitionTo(
          this.pinnedPlan ? 'settings.billing.payment-methods' : 'settings.billing.your-plan',
        );
        this.alerts.success('subscription.subscribe.success', successMessage);
      } catch (res) {
        // error can be returned in more than one format: handle all cases
        let error = res?.errors?.[0] || res;
        let errorMessage =
          error.message ||
          error.detail ||
          'An error occurred. Please contact win@customer.io for help setting up your subscription.';

        this.alerts.remove('subscription.subscribe.inProgress');
        this.alerts.error('subscription.subscribe.error', errorMessage);
      }
    },
  );

  subscribeToPipelinesStarterTask = task({ drop: true }, async () => {
    try {
      await this.api.post(`subscribe_pipelines`, {
        pathScope: 'account',
      });

      await this.pollForSubscriptionTask.perform('exists');

      await this.initialize({ refresh: true });
      this.alerts.success(
        'subscription.subscribe.success',
        "Your account is ready to go. You're now subscribed to the Data Pipelines Starter plan.",
      );
      return true;
    } catch {
      let errorMessage = 'An error occurred. Please contact win@customer.io for help setting up your subscription.';

      this.alerts.error('subscription.subscribe.error', errorMessage);

      return false;
    }
  });

  cancelAccount = task({ drop: true }, async (feedback: string) => {
    let confirmed = window.confirm("Are you sure you'd like to cancel your account?");

    if (!confirmed) {
      return;
    }

    this.alerts.sticky('account.cancel.inProgress', 'Canceling account in progress, please wait …');
    let transitionToRoute = 'post_cancel';
    if (this.account.current.state === 'suspended' || this.account.current.state === 'overdue') {
      transitionToRoute = 'account_canceled';
    }
    return this.account.current
      .cancel({ feedback })
      .then(async () => await timeout(config.APP.feedbackDelay))
      .then(() => this.store.unloadAll('account')) // Unload account data from store
      .then(() => {
        this.alerts.remove('account.cancel.inProgress');
        this.alerts.success('account.cancel.success', 'Account canceled');
        this.router.transitionTo(transitionToRoute, { queryParams: { id: this.account.current?.id } });
      })
      .catch(() => {
        this.alerts.remove('account.cancel.inProgress');
        this.alerts.error(
          'account.cancel.error',
          'An error occurred. Please contact win@customer.io for help canceling your account.',
        );
      });
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    billing: BillingService;
  }
}
